import ParticipantDocumentContext from "@/contexts/data/ParticipantDocumentContext";
import useGetProcessVerificationDataByDocumentId from "@/hooks/data/useGetProcessVerificationDataByDocumentId";
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogProps, FormControl, FormControlLabel, FormGroup, TextField, useTheme } from "@mui/material";
import { ChangeEvent, FC, useContext, useEffect, useMemo, useState } from "react";

interface IRedactSelectDialogProps extends DialogProps {
    closeDialog: (...args: any) => any,
    onAfterClose: (selectedValues: string[], comments: string) => any
}

const RedactSelectDialog:FC<IRedactSelectDialogProps> = ({
    closeDialog,
    onAfterClose,
    open
}) => {
    const checkboxOptions = useMemo(() => [
        "Name/Initials",
        "Date of Birth",
        "Address",
        "Phone Number",
        "MR/UR Number",
        "Email Address",
        "Next of Kin (names and contact details)",
        "Lab ID",
        "Patient ID (Radiology Forms)",
        "Missing Patient ID",
        "Wrong Patient ID",
        "Missing Page",
        "Poor Redaction",
        "Non-trial staff name and contact details",
        "Other"
    ], []);

    const document = useContext(ParticipantDocumentContext);
    const {data: processVerificationEvents, refresh: refreshProcessVerificationEvents} = useGetProcessVerificationDataByDocumentId({documentId: document?.id});
    
    const [selected, setSelected] = useState<string[]>([]);
    const [comments, setComments] = useState<string>("");

    useEffect(() => {
        console.log("processVerificationEvents changed: ", processVerificationEvents);
        if (open) {

            console.log("processVerificationEvents on open: ", processVerificationEvents);
            setSelected(processVerificationEvents?.redactedFields ?? []);
            setComments(processVerificationEvents?.comments ?? "");
        }
    }, [open, processVerificationEvents])

    const theme = useTheme();

    const checkBoxes = useMemo(() => checkboxOptions.map((option, index) =>
        <FormControlLabel
            key={index}
            label={option}
            control={
                <Checkbox
                    onChange={(_event, checked) => handleSelectChange(option, checked)}
                    checked={selected.includes(option)}
                
                />
            }
            sx={{
                "& .MuiFormControlLabel-label": {
                    color: theme.palette.text.primary
                }
            }}
        />
    ), [checkboxOptions, selected]);

    const handleSelectChange = (option: string, checked: boolean) => {
        if(!checked) {
            setSelected(old => old.filter(selectedOption => selectedOption != option));
        } 
        else {
            setSelected(old => [...old, option]);
        }
    }


    const handleCommentsChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setComments(event.target.value);
    }

    const handleConfirm = async () => {
        closeDialog();
        await onAfterClose(selected, comments);
        if (refreshProcessVerificationEvents) {
            await refreshProcessVerificationEvents();
        }
    }

    const handleCancel = () => {
        closeDialog();
    }

    return (
        <Dialog 
            open={open}
            onClose={handleCancel}
            maxWidth={"sm"}
            fullWidth
        >
            <DialogContent>
                <FormGroup>
                    {checkBoxes}
                </FormGroup>
                <TextField
                    sx={{
                        '& .MuiInputLabel-root': { color: theme.palette.mode === 'dark' ? 'white' : 'black' }
                    }}
                    label={"Comments for Site"}
                    multiline
                    rows={5}
                    onChange={handleCommentsChange}
                    value={comments}
                    fullWidth
                />
                <DialogActions>
                    <Button
                        variant={"contained"}
                        onClick={handleConfirm}
                    >
                        Confirm
                    </Button>
                    <Button
                        variant={"contained"}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}

export default RedactSelectDialog;